import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    VpnKey as PasswordIcon,
    PersonAdd as PersonAddIcon,
    Star as LeaderIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import api from '../api/axios';

const DepartmentUsers = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [department, setDepartment] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Add User Dialog
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addForm, setAddForm] = useState({ name_en: '', name_ar: '', email: '', password: '' });

    // Edit User Dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({ name_en: '', name_ar: '', email: '' });

    // Reset Password Dialog
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwordUser, setPasswordUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');

    // Delete Confirmation Dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteUser, setDeleteUser] = useState(null);

    useEffect(() => {
        fetchDepartmentUsers();
    }, []);

    const fetchDepartmentUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/departments/my-users');
            // axios interceptor returns response.data, so we access .data directly
            setDepartment(response.data.department);
            setUsers(response.data.users || []);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load department users');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async () => {
        try {
            await api.post('/departments/my-users', addForm);
            setSuccess('User added successfully');
            setAddDialogOpen(false);
            setAddForm({ name_en: '', name_ar: '', email: '', password: '' });
            fetchDepartmentUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add user');
        }
    };

    const handleEditUser = async () => {
        try {
            await api.put(`/departments/my-users/${editUser.id}`, editForm);
            setSuccess('User updated successfully');
            setEditDialogOpen(false);
            fetchDepartmentUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update user');
        }
    };

    const handleResetPassword = async () => {
        try {
            await api.put(`/departments/my-users/${passwordUser.id}/password`, { password: newPassword });
            setSuccess('Password reset successfully');
            setPasswordDialogOpen(false);
            setNewPassword('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password');
        }
    };

    const handleDeleteUser = async () => {
        try {
            await api.delete(`/departments/my-users/${deleteUser.id}`);
            setSuccess('User removed successfully');
            setDeleteDialogOpen(false);
            fetchDepartmentUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove user');
        }
    };

    const openEditDialog = (user) => {
        setEditUser(user);
        setEditForm({ name_en: user.name_en, name_ar: user.name_ar || '', email: user.email || '' });
        setEditDialogOpen(true);
    };

    const openPasswordDialog = (user) => {
        setPasswordUser(user);
        setNewPassword('');
        setPasswordDialogOpen(true);
    };

    const openDeleteDialog = (user) => {
        setDeleteUser(user);
        setDeleteDialogOpen(true);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Department Users
                    </Typography>
                    {department && (
                        <Typography variant="subtitle1" color="text.secondary">
                            {department.name_en} {department.name_ar && `/ ${department.name_ar}`}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setAddDialogOpen(true)}
                >
                    Add User
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{
                            '& .MuiTableCell-root': {
                                backgroundColor: '#1a3a6b !important',
                                color: 'white !important',
                                fontWeight: 'bold'
                            }
                        }}>
                            <TableCell>Name (EN)</TableCell>
                            <TableCell>Name (AR)</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography color="text.secondary" py={4}>
                                        No users in this department
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((u) => (
                                <TableRow key={u.id} hover>
                                    <TableCell>{u.name_en}</TableCell>
                                    <TableCell>{u.name_ar || '-'}</TableCell>
                                    <TableCell>{u.email || '-'}</TableCell>
                                    <TableCell>
                                        {u.is_department_leader ? (
                                            <Chip icon={<LeaderIcon />} label="Leader" color="primary" size="small" />
                                        ) : (
                                            <Chip label="User" variant="outlined" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit User">
                                            <IconButton color="primary" onClick={() => openEditDialog(u)} size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reset Password">
                                            <IconButton color="warning" onClick={() => openPasswordDialog(u)} size="small">
                                                <PasswordIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {!u.is_department_leader && u.id !== user?.id && (
                                            <Tooltip title="Remove User">
                                                <IconButton color="error" onClick={() => openDeleteDialog(u)} size="small">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add User Dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name (English)"
                        fullWidth
                        margin="normal"
                        value={addForm.name_en}
                        onChange={(e) => setAddForm({ ...addForm, name_en: e.target.value })}
                        required
                    />
                    <TextField
                        label="Name (Arabic)"
                        fullWidth
                        margin="normal"
                        value={addForm.name_ar}
                        onChange={(e) => setAddForm({ ...addForm, name_ar: e.target.value })}
                    />
                    <TextField
                        label="Email"
                        type="email"
                        fullWidth
                        margin="normal"
                        value={addForm.email}
                        onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={addForm.password}
                        onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                        required
                        helperText="Minimum 6 characters"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddUser} disabled={!addForm.name_en || addForm.password.length < 6}>
                        Add User
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name (English)"
                        fullWidth
                        margin="normal"
                        value={editForm.name_en}
                        onChange={(e) => setEditForm({ ...editForm, name_en: e.target.value })}
                        required
                    />
                    <TextField
                        label="Name (Arabic)"
                        fullWidth
                        margin="normal"
                        value={editForm.name_ar}
                        onChange={(e) => setEditForm({ ...editForm, name_ar: e.target.value })}
                    />
                    <TextField
                        label="Email"
                        type="email"
                        fullWidth
                        margin="normal"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditUser} disabled={!editForm.name_en}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reset Password for {passwordUser?.name_en}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="New Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        helperText="Minimum 6 characters"
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleResetPassword} disabled={newPassword.length < 6}>
                        Reset Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Remove User</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove <strong>{deleteUser?.name_en}</strong> from this department?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteUser}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DepartmentUsers;
